import React, { useState } from "react";
import { Table, Select, Button, Spin } from "antd";
import { useGetJobTitlesAnalyticsQuery } from "../../services/analytics";
import { useGetEventsQuery } from "../../services/events";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import * as XLSX from "xlsx";

const { Option } = Select;

const JobTitles = () => {
    const [selectedEvent, setSelectedEvent] = useState(null);

    const { data: eventsData, isLoading: isLoadingEvents } = useGetEventsQuery({
        currentPage: 1,
        resultPerPage: 1000
    });

    const { data: jobData, isFetching: isLoadingJobs } = useGetJobTitlesAnalyticsQuery({
        eventId: selectedEvent
    });

    const events = eventsData?.events?.data || [];
    const tableData = jobData?.data || [];

    const handleExport = () => {
        if (!tableData || tableData.length === 0) return;
        const exportData = tableData.map(item => ({
            "Job Title": item._id,
            "Total Events": item.totalEvents || 0,
            "Total Meeting Requests": item.totalMeetings || 0,
            "Accepted Meetings": item.acceptedMeetings || 0,
            "Rejected Meetings": item.rejectedMeetings || 0,
            "Pending Meetings": item.pendingMeetings || 0
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Job Title Analytics");
        XLSX.writeFile(wb, "Job_Title_Analytics_Report.xlsx");
    };

    const columns = [
        {
            title: 'Job Title / Designation',
            dataIndex: '_id',
            key: 'jobTitle',
            render: text => <span className="font-semibold text-gray-700 capitalize">{text}</span>
        },
        {
            title: 'Total Events',
            dataIndex: 'totalEvents',
            key: 'totalEvents',
            sorter: (a, b) => (a.totalEvents || 0) - (b.totalEvents || 0)
        },
        {
            title: 'Total Meeting Requests',
            dataIndex: 'totalMeetings',
            key: 'totalMeetings',
            sorter: (a, b) => (a.totalMeetings || 0) - (b.totalMeetings || 0)
        },
        {
            title: 'Accepted',
            dataIndex: 'acceptedMeetings',
            key: 'acceptedMeetings',
            sorter: (a, b) => (a.acceptedMeetings || 0) - (b.acceptedMeetings || 0)
        },
        {
            title: 'Rejected',
            dataIndex: 'rejectedMeetings',
            key: 'rejectedMeetings',
            sorter: (a, b) => (a.rejectedMeetings || 0) - (b.rejectedMeetings || 0)
        },
        {
            title: 'Pending',
            dataIndex: 'pendingMeetings',
            key: 'pendingMeetings',
            sorter: (a, b) => (a.pendingMeetings || 0) - (b.pendingMeetings || 0)
        },
    ];

    // Chart data for top 15 job titles
    const chartData = tableData.slice(0, 15).map(item => ({
        name: item._id,
        Events: item.totalEvents || 0,
        Requests: item.totalMeetings || 0,
        Accepted: item.acceptedMeetings || 0,
        Rejected: item.rejectedMeetings || 0,
        Pending: item.pendingMeetings || 0,
    }));

    return (
        <div className="animate-fade-in">
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] gap-4">
                <div className="flex items-center w-full sm:w-auto">
                    <span className="mr-4 font-semibold text-gray-700 whitespace-nowrap">Filter by Event (Optional):</span>
                    <Select
                        allowClear
                        className="w-full sm:w-[350px]"
                        placeholder="All Events (Overall)"
                        onChange={(val) => setSelectedEvent(val)}
                        loading={isLoadingEvents}
                        showSearch
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {events.map((e) => (
                            <Option key={e._id} value={e._id}>{e.name}</Option>
                        ))}
                    </Select>
                </div>
                <Button
                    type="primary"
                    disabled={!tableData || tableData.length === 0}
                    onClick={handleExport}
                    className="bg-primary hover:bg-yellow-600 border-none shadow-md hover:shadow-lg transition-all w-full sm:w-auto mt-4 sm:mt-0"
                >
                    Export Data
                </Button>
            </div>

            {isLoadingJobs ? (
                <div className="flex justify-center p-20"><Spin size="large" /></div>
            ) : tableData.length > 0 ? (
                <div className="space-y-8">
                    <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300" style={{ height: 'max(500px, 60vh)' }}>
                        <h3 className="font-semibold text-xl text-gray-800 mb-6 border-b pb-4">Top 15 Most Requested Job Titles</h3>
                        <ResponsiveContainer width="100%" height="80%">
                            <BarChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 10, bottom: 40 }}
                                layout="vertical"
                            >
                                <XAxis
                                    type="number"
                                    tick={{ fill: '#8c8c8c' }}
                                    axisLine={{ stroke: '#f0f0f0' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    width={180}
                                    tick={{ fill: '#595959', fontSize: 12, fontWeight: 500 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    cursor={{ fill: '#f0f2f5', opacity: 0.4 }}
                                    contentStyle={{
                                        borderRadius: '12px',
                                        border: '1px solid #f0f0f0',
                                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                                    }}
                                />
                                <Legend />
                                <Bar dataKey="Events" fill="#91d5ff" barSize={18} />
                                <Bar dataKey="Requests" fill="#1890ff" barSize={18} />
                                <Bar dataKey="Accepted" fill="#52c41a" barSize={18} />
                                <Bar dataKey="Pending" fill="#faad14" barSize={18} />
                                <Bar dataKey="Rejected" fill="#f5222d" barSize={18} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
                        <Table
                            dataSource={tableData}
                            columns={columns}
                            rowKey="_id"
                            pagination={{ pageSize: 15, className: 'px-4 mb-4' }}
                            rowClassName="hover:bg-gray-50/50 transition-colors cursor-pointer"
                        />
                    </div>
                </div>
            ) : (
                <div className="text-center text-gray-400 mt-20 flex flex-col items-center">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="mb-4 text-gray-300">
                        <path d="M20 6H16V4C16 2.9 15.1 2 14 2H10C8.9 2 8 2.9 8 4V6H4C2.9 6 2.01 6.9 2.01 8L2 19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V8C22 6.9 21.1 6 20 6ZM10 4H14V6H10V4ZM20 19H4V17H20V19ZM20 14H4V8H20V14Z" fill="currentColor" />
                    </svg>
                    <p className="text-lg">No job-title metrics found.</p>
                </div>
            )}
        </div>
    );
};

export default JobTitles;
